import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Select from "react-select";
import { getUsersByFranchiseId } from '@app/services/user';
import { assignUserToCentre } from '@app/services/centre';
import { Button } from '@app/components';
import { Modal } from 'react-bootstrap';

const AssignExistingUserModal = (props: any) => {

    const [users, setUsers] = useState<any[]>([]);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [selectedRoleId, setSelectedRoleId] = useState('');
    const [isLoading, setLoading] = useState(false);
    const [userError, setUserError] = useState('');
    const [roleError, setRoleError] = useState('');

    useEffect(() => {
        if (props.show && props.franchiseId) loadUsersWithRoles(props.franchiseId);
    }, [props.show, props.franchiseId]);

    // Roles this user already holds AT THIS CENTRE specifically (not just
    // anywhere), sourced from ViewCentre's centre-scoped staff list. A user
    // holding several centre roles overall (e.g. Centre Manager AND Mentor)
    // should stay assignable here for whichever of those roles isn't yet
    // attached to this centre -- excluding the whole user just because one
    // role is already assigned here would hide the rest.
    const rolesAlreadyAtThisCentre = (userId: number) => {
        const existing = (props.existingStaff || []).find((u: any) => u.id === userId);
        return existing?.roles || [];
    };

    const loadUsersWithRoles = async (franchiseId: any) => {
        try {
            const centreRoleSlugs = (props.centreRoles || []).map((r: any) => r.slug);
            const resp = await getUsersByFranchiseId(franchiseId, centreRoleSlugs);
            if (resp?.success === true) {
                // Keep only users who hold at least one centre role that
                // isn't already assigned to them at this centre.
                const filtered = (resp.collections || []).filter((u: any) => {
                    const alreadyHeldIds = rolesAlreadyAtThisCentre(u.id).map((r: any) => r.id);
                    return u.roles?.some((r: any) =>
                        props.centreRoles?.some((cr: any) => cr.id === r.id) && !alreadyHeldIds.includes(r.id)
                    );
                });
                setUsers(filtered);
            }
        } catch (error: any) {
            toast.error(error?.message || 'Failed to load users');
        }
    };

    // Only offer roles the user actually holds (globally) that aren't
    // already assigned to them at this specific centre.
    const availableRoles = (props.centreRoles || []).filter((cr: any) => {
        const alreadyHeldIds = rolesAlreadyAtThisCentre(selectedUser?.id).map((r: any) => r.id);
        return selectedUser?.roles?.some((r: any) => r.id === cr.id) && !alreadyHeldIds.includes(cr.id);
    });

    const handleAssign = async () => {
        setUserError('');
        setRoleError('');
        let hasError = false;
        if (!selectedUser) { setUserError('Please select a user'); hasError = true; }
        if (!selectedRoleId) { setRoleError('Please select a role'); hasError = true; }
        if (hasError) return;
        try {
            setLoading(true);
            const resp = await assignUserToCentre({
                user_id: selectedUser.id,
                centre_id: props.centreId,
                franchise_id: props.franchiseId,
                role_id: parseInt(selectedRoleId),
            });
            if (resp?.success === true) {
                toast.success(resp?.message || 'User assigned successfully');
                handleClose();
                props.onSuccess?.();
            } else {
                toast.error(resp?.errors?.[0]?.msg || resp?.message || 'Failed to assign user');
            }
        } catch (error: any) {
            toast.error(error?.message || 'Internal server error');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSelectedUser(null);
        setSelectedRoleId('');
        setUserError('');
        setRoleError('');
        props.handleClose();
    };

    return (
        <Modal show={props.show} onHide={handleClose} size="sm">
            <Modal.Header closeButton>
                <Modal.Title>Assign Existing User</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="form-group">
                    <label>Select User <span className="required">*</span></label>
                    <Select
                        placeholder="Search and select user"
                        options={users.map((u: any) => ({ value: u.id, label: `${u.first_name} ${u.last_name} (${u.email})`, user: u }))}
                        onChange={(opt: any) => { setSelectedUser(opt ? opt.user : null); setSelectedRoleId(''); }}
                        value={selectedUser ? { value: selectedUser.id, label: `${selectedUser.first_name} ${selectedUser.last_name} (${selectedUser.email})` } : null}
                        isClearable
                    />
                    {userError && <div className="text-danger mt-1">{userError}</div>}
                </div>
                <div className="form-group mb-0">
                    <label>Assign Role <span className="required">*</span></label>
                    <select
                        className="form-control"
                        value={selectedRoleId}
                        onChange={(e) => setSelectedRoleId(e.target.value)}
                        disabled={!selectedUser}
                    >
                        <option value="">{selectedUser ? '-- Please select --' : 'Select a user first'}</option>
                        {availableRoles.map((r: any) => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                    </select>
                    {selectedUser && availableRoles.length === 0 && (
                        <div className="text-muted small mt-1">This user doesn't hold any centre role that can be assigned here.</div>
                    )}
                    {roleError && <div className="text-danger mt-1">{roleError}</div>}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={handleAssign} isLoading={isLoading} className="btn btn-primary mr-2">Assign</Button>
                <Button className="btn btn-default" onClick={handleClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AssignExistingUserModal;
